import React, { useContext, useEffect, useState } from 'react';
import {
  Button, Grid, TextField, Typography, TableCell, TableRow, TableContainer, Table, TableBody,
  DialogActions, Box, FormControlLabel, Checkbox, FormGroup, DialogContent, Dialog, DialogTitle,
  IconButton, useTheme,
} from '@mui/material';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { GetItemsResponse, Item } from '../../../../app/services/api/generated';
import { SearchSm } from '../../../../assets';
import useLogError from '../../../../hooks/useLogError';
import { StockCountZonesContext, StockCountZonesContextValues } from '../../contexts/StockCountZonesContext';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import CustomPagination from '../../../../components/forms/Pagination';

const StockCountZonesCreateItemsModal = () => {
  const {
    t,
    isItemsModalOpen,
    itemsData,
    handleClose,
    handleItems,
    firstSelectedStore,
  } = useContext(StockCountZonesContext) as StockCountZonesContextValues;

  const theme = useTheme();
  const { getItems } = useNswagClient();
  const { logError } = useLogError();

  const [apiData, setApiData] = useState<GetItemsResponse | undefined>();
  const [tempData, setTempData] = useState<Item[]>([]);
  const [search, setSearch] = useState('');
  const [isContinueSelecting, setIsContinueSelecting] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [lastStoreNumber, setLastStoreNumber] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState<number | undefined>();

  const rowsPerPage = 100;

  // Fetch items only when firstSelectedStore or page changes
  useEffect(() => {
    if (firstSelectedStore?.storeNumber && firstSelectedStore.storeNumber !== lastStoreNumber) {
      setSearch('');
      setLastStoreNumber(firstSelectedStore.storeNumber);
      setPage(0);
      getItemData('', 0);
    }
  }, [firstSelectedStore?.storeNumber]);

  useEffect(() => {
    if (firstSelectedStore?.storeNumber) {
      getItemData(search, page);
    }
  }, [page]);

  // Fetch items for the selected store, search term, and page
  const getItemData = (searchTerm: string, pageNum: number) => {
    const storeNumber = firstSelectedStore?.storeNumber ?? '';
    if (!storeNumber) {
      setApiData(undefined);
      setCount(0);
      return;
    }
    setIsLoadingItems(true);
    const skip = pageNum * rowsPerPage;
    getItems(
      searchTerm,
      storeNumber,
      undefined,
      undefined,
      undefined,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      'description',
      'asc',
      skip,
      rowsPerPage,
    )
      .then((result) => {
        if (result) {
          setApiData(result.data);
          setCount(result.data?.itemCount ?? 0);
        } else {
          logError(result);
        }
      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => setIsLoadingItems(false));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    getItemData(search, 0);
  };

  const addItemToTempList = (item: Item) => {
    if (!tempData?.some(e => e.id == item.id)) {
      setTempData([...tempData ?? [], item]);
    }
  };

  const handleConfirm = () => {
    handleItems(tempData || []);
    !isContinueSelecting && close();
  };

  const handleItemChange = (item: number, isChecked: boolean) => {
    if (isChecked) {
      const selecteditem = apiData?.items?.find(row => row.id === item);
      if (selecteditem) {
        addItemToTempList(selecteditem);
      }
    } else {
      setTempData(prevData => prevData?.filter(row => row.id !== item) || []);
    }
  };

  const close = () => {
    setTempData([]);
    handleClose();
  };

  const toggleCheckbox = () => {
    setIsContinueSelecting(!isContinueSelecting);
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      setTempData(apiData?.items ?? []);
    } else {
      setTempData([]);
    }
  };

  const maxPages = () => count ? Math.ceil(count / rowsPerPage) : 0;

  return (
    <Dialog open={isItemsModalOpen}
      onClose={close}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('messages.itemsLookup')}
        <IconButton aria-label={t('modals.close')}
          onClick={close}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Grid
            container
            spacing={2}
            display='flex'
            flexWrap={'nowrap'}
            alignItems='self-end'
            sx={{ borderTop: '1px solid', borderColor: 'gray' }}
          >
            <Grid item
              xs={10}
              sx={{ pt: '5px' }}>
              <TextField
                fullWidth
                placeholder={t('modals.searchPlaceholder')}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}
                value={search}
                onChange={handleSearchChange} />
            </Grid>
            <Grid item
              xs={2}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
              <Button size='lg'
                aria-label={t('modals.search')}
                startIcon={<SearchSm />}
                onClick={handleSearch} />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ pt: '10px' }}>
          <Typography
            variant='textSM'
            sx={{ color: theme.palette.custom.gray[500] }}>{t('modals.search')}</Typography>
        </Box>
        <Box sx={{ width: '100%', textAlign: 'left', border: '1px solid', maxHeight: 400, overflowY: 'auto', position: 'relative', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableBody>
                {isLoadingItems ? (
                  <TableRow>
                    <TableCell align="center"
                      colSpan={2}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : (
                  firstSelectedStore?.storeNumber && apiData?.items?.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell style={{ display: 'flex', alignItems: 'center' }} >
                        <Checkbox
                          color="primary"
                          checked={tempData?.some((item) => item.itemNumber === row.itemNumber) || itemsData.some((item) => item.itemNumber === row.itemNumber)}
                          onChange={(e) => handleItemChange(row.id!, e.target.checked)}
                        />
                        <Grid style={{ marginLeft: '8px' }}>
                          {row.itemNumber}<br />
                          {row.description}
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
          />
        </Box>
        <FormGroup sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  !!apiData?.items?.length && tempData.length === apiData.items.length
                }
                onChange={handleSelectAllChange}
              />
            }
            label={t('modals.selectAll')}
          />
          <FormControlLabel
            control={<Checkbox />}
            label={t('modals.continueSearching')}
            checked={isContinueSelecting}
            onChange={toggleCheckbox}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <Button
          variant="secondary"
          size='lg'
          onClick={close}>
          {t('modals.cancel')}
        </Button>
        <Button
          size='lg'
          onClick={handleConfirm}>
          {t('modals.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCountZonesCreateItemsModal;